/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import {
	CardContent,
	Divider,
	TextField,
	FormHelperText,
	CircularProgress,
	FormLabel,
	Container,
	Grid,
	useTheme,
} from '@mui/material';
import { Formik, Form, useFormikContext } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { validationSchema } from './validationSchema';
import { default as folderValidationSchema } from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useCreateClaimMutation } from '@/features/claims/ticketsApi';
import { PolicySelect } from '@/components/selects/PolicySelect';
import { PolicyTransactionSelect } from '@/components/selects/PolicyTransactionSelect';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import FileUpload from '@/components/FileUpload';
import { PageHeader } from '@/components/layouts/PageHeader';
import { showToast } from '@/features/toast/toastSlice';
import { useLocation } from 'react-router-dom';
import { ClaimTypeSelect } from '@/components/selects/ClaimTypeSelect';
import { FormikErrorScroll, useFormikHelper } from '@/hooks/useFormikHelper';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import { paths } from '@/app/routes';
import { getPipelineIcon } from '@/utils/constants';
import {
	BROKER_GROUP_ID,
	DEAL_OVERVIEW,
	OWNER_DOC_ID,
	YEAR_FOLDER,
} from '../CreateNewBusinessForm/validationSchema';
import { FOLDER_NAME } from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import DealNaming from '../fields/DealNaming';
import FormNavigationBlocker from '../FormNavigationBlocker';
import { useGetInsightPolicyQuery } from '@/features/policies/policiesApi';
import { extractMidnightUTCTimestamp, getYearFolderFormat } from '@/utils/date';
import { uniqueId } from 'lodash';
import DealFormActions from '@/components/forms/CreateFormFields/DealFormActions';
import DealFormMessages from '@/components/forms/CreateFormFields/DealFormMessages';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';

const CreateClaimForm = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const account = useSelector((state) => state.msalAccount.account);

	const existingClient = useMemo(() => location?.state?.client ?? {}, [location?.state?.client]);

	const policyId = useMemo(() => location?.state?.policyId ?? {}, [location?.state?.policyId]);
	const { data: policy } = useGetInsightPolicyQuery(policyId, { skip: !policyId });
	const existingPolicy = useMemo(
		() => location?.state?.policy ?? policy ?? {},
		[location?.state?.policy, policy],
	);
	const existingTransaction = location?.state?.transaction;

	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [formLoading, setFormLoading] = useState(false);
	const [brokerGroupId, setBrokerGroupId] = useState(userDetails?.brokerGroupId);

	useEffect(() => {
		if (!brokerGroupId && userDetails?.brokerGroupId)
			setBrokerGroupId(userDetails.brokerGroupId);
	}, [brokerGroupId, userDetails?.brokerGroupId]);

	const initialValues = useMemo(() => {
		return {
			policy: existingPolicy ?? {},
			policyId: existingPolicy?.Id,
			transaction: existingTransaction,
			policyTransactionId: existingTransaction?.Id,
			files: [],
			describeClaim: '',
			notes: '',
			clientName: existingClient?.name ?? '',
			successMessage: '',
			errorMessage: '',
			dealName: '',
			[FOLDER_NAME]: '',
			[DEAL_OVERVIEW]: '',
			[YEAR_FOLDER]: getYearFolderFormat(
				existingTransaction?.FromDate ?? existingTransaction?.PolicyFromDate,
			),
			dateOfLoss: null,
			dateReported: null,
			claimType: '',
			subjectOfLossDetails: '',
			repairer: '',
			assessor: '',
			insurer: existingPolicy?.InsurerName ?? '',
			clientId: existingClient?.clientId,
			[OWNER_DOC_ID]: userDetails?.userId,
			[BROKER_GROUP_ID]: existingClient?.brokerGroupId ?? userDetails?.brokerGroupId,
		};
	}, [
		existingPolicy,
		existingTransaction,
		existingClient,
		userDetails?.userId,
		userDetails?.brokerGroupId,
	]);

	const [schema, setSchema] = useState(validationSchema);

	const { setUpDealFilesAndFolders } = useClientStorageHooks(brokerGroupId);

	const [createClaim] = useCreateClaimMutation();

	const createNewClaim = async (values) => {
		const body = {
			ownerDocId: values[OWNER_DOC_ID] ?? userDetails?.userId,
			dealName: values[FOLDER_NAME].trim(),
			dealOverview: (values[DEAL_OVERVIEW] ?? '').trim(),
			description: values.describeClaim,
			clientId: values.clientId,
			policyId: values.policyId,
			policyTransId: values.policyTransactionId,
			insurer: values.insurer,
			claimType: values.claimType,
			subjectOfLossDetails: values.subjectOfLossDetails || '',
			...(values.notes && { notes: values.notes }),
			...(values.dateOfLoss && {
				dateOfLoss: extractMidnightUTCTimestamp(values.dateOfLoss),
			}),
			...(values.dateReported && {
				dateReported: extractMidnightUTCTimestamp(values.dateReported),
			}),
			yearFolder: values[YEAR_FOLDER],
			repairer: values.repairer || '',
			assessor: values.assessor || '',
			brokerGroupId: values[BROKER_GROUP_ID],
		};

		console.log('Create Claim BODY', body);
		return await createClaim(body)
			.unwrap()
			.catch((err) => {
				console.log('🚀 ~ Error creating claim', err);
				// Capture INSIGHT error message (e.g., when SubjectOfLoss is required but empty)
				const errorMessage = err?.data?.message || err?.message || 'Error creating claim';
				// Return error to be displayed to user via createError() in onSubmit
				return { error: { message: errorMessage, data: err?.data } };
			});
	};

	const createClaimFolders = async (values, dealId, noteId) => {
		try {
			await setUpDealFilesAndFolders({
				dealId,
				noteId,
				clientId: values.clientId,
				clientName: values.clientName,
				dealName: values[FOLDER_NAME],
				pipelineId: process.env.REACT_APP_PIPELINE_CLAIMS,
				files: values.files,
				notes: values.notes,
				yearFolder: values.yearFolder,
			});
		} catch (error) {
			console.log('🚀 ~ Error creating claim folders', error);
			return {
				error: {
					message: `Claim ${dealId} created, but there was an error creating claim folder or uploading files`,
				},
			};
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validateOnChange={false}
			validationSchema={schema}
			enableReinitialize
			onSubmit={async (values, { resetForm, setFieldValue, setSubmitting }) => {
				setFormLoading(true);
				console.log('📋 ~ Formik values', values);
				const createError = (message) => {
					setFieldValue('errorMessage', message ?? 'Error creating claim');
					setFieldValue('successMessage', '');
					setSubmitting(false);
					setFormLoading(false);
				};

				const toastId = uniqueId();
				dispatch(
					showToast({
						toastId,
						message: 'Creating claim...',
						action: {
							path: `${paths.claims}`,
							label: 'GO BACK TO ALL CLAIMS',
						},
						autohide: false,
					}),
				);

				const res = await createNewClaim(values);
				if (res?.error) {
					console.log('🙅 ~ Error creating claim', res?.error);
					createError(res?.error?.message);
					return;
				}
				const response = await createClaimFolders(values, res.dealId, res.noteId);
				if (response?.error) {
					createError(response?.error?.message);
					dispatch(
						showToast({
							toastId,
							message:
								response?.error?.message ?? `${values[FOLDER_NAME]} Claim created`,
							action: {
								path: `${paths.claims}/${res.dealId}`,
								label: 'GO TO CLAIM',
							},
							autohide: false,
						}),
					);
					return;
				}
				console.log('🚀 ~ Successfully created new claim', res);

				dispatch(
					showToast({
						toastId,
						message: `${values[FOLDER_NAME]} Claim created!`,
						action: {
							path: `${paths.claims}/${res.dealId}`,
							label: 'GO TO CLAIM',
						},
						autohide: false,
					}),
				);

				const isOnCreateClaim = window.location.pathname.includes('/create-claim');
				if (isOnCreateClaim) {
					window.scrollTo(0, 0);
					window.history.replaceState({}, '');
					resetForm();
					setFieldValue('successMessage', 'Successfully created claim');
					setFieldValue('errorMessage', '');
					setSubmitting(false);
					setFormLoading(false);
				}
			}}
		>
			{({ isSubmitting }) => (
				<CreateClaimFormBody
					{...{
						loading: isSubmitting || formLoading,
						setSchema,
						setBrokerGroupId,
					}}
				/>
			)}
		</Formik>
	);
};

export default CreateClaimForm;

const CreateClaimFormBody = ({ loading, setSchema, setBrokerGroupId }) => {
	const theme = useTheme();
	const location = useLocation();
	const account = useSelector((state) => state.msalAccount.account);

	const existingClient = useMemo(() => location?.state?.client ?? {}, [location?.state]);
	const isExistingClient = useMemo(
		() => Object.keys(existingClient).length > 0,
		[existingClient],
	);
	const existingTransaction = location?.state?.transaction;

	const email = account?.username;

	const { values, isSubmitting, setFieldValue, setValues, getFieldProps, handleChange } =
		useFormikContext();
	const { getError, getErrorMessage } = useFormikHelper();

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });
	const isUserLoading = userQuery.isLoading || userQuery.isUninitialized;

	useEffect(() => {
		setSchema(
			validationSchema
				.omit([FOLDER_NAME])
				.concat(folderValidationSchema(values.existingFolders, 'Deal name', 'claim')),
		);
	}, [values.existingFolders]);

	const { data: clientRow } = useGetClientRowQuery(values.clientId, {
		skip: !values.clientId,
	});

	useEffect(() => {
		if (clientRow?.brokerGroupId) setBrokerGroupId(clientRow?.brokerGroupId);
	}, [clientRow?.brokerGroupId]);

	return (
		<Container sx={{ pt: 3 }}>
			<Form>
				<FormNavigationBlocker />
				<FormikErrorScroll isSubmitting={isSubmitting} />
				<PageHeader
					title={'Report a Claim'}
					icon={getPipelineIcon(
						process.env.REACT_APP_PIPELINE_CLAIMS,
						'40px',
						theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
					)}
				/>
				<CardContent>
					{isUserLoading ? (
						<CircularProgress />
					) : (
						<Grid container rowSpacing={1} columnSpacing={2}>
							<DealNaming dealName='Claim name' />
							<Grid size={'grow'}>
								<FormLabel required>{'Search client name'}</FormLabel>
								<InstantClientSelect
									clientId={values.clientId}
									clientName={values.clientName}
									filterProspects={true}
									setClient={(client) => {
										console.log('client', client);
										setValues({
											...values,
											clientId: client?.id,
											clientName: client?.name,
											policy: {},
											policyId: null,
											transaction: {},
											policyTransactionId: null,
											[YEAR_FOLDER]: null,
											[BROKER_GROUP_ID]:
												client?.brokerGroupId ?? userDetails?.brokerGroupId,
										});
									}}
									disabled={loading || isExistingClient}
								/>
								<FormHelperText error={getError('clientId')}>
									{getErrorMessage('clientId')}
								</FormHelperText>
							</Grid>
							<Grid size={4}>
								<FormLabel>{'Assign to'}</FormLabel>
								<OwnerSelect
									initialId={values[OWNER_DOC_ID]}
									disabled={isSubmitting}
									showGroup={true}
									onChange={(user) => {
										setFieldValue(OWNER_DOC_ID, user?.id ?? null);
									}}
								/>
							</Grid>
							<Grid size={12}>
								<FormLabel required sx={{ display: 'flex' }}>
									{'Policy'}
								</FormLabel>
								<PolicySelect
									clientId={values.clientId}
									policy={values.policy}
									setPolicy={(newPolicy) => {
										setValues({
											...values,
											policy: newPolicy,
											policyId: newPolicy?.Id,
											transaction: {},
											policyTransactionId: null,
											[YEAR_FOLDER]: null,
										});
									}}
									disabled={loading}
								/>
								{!values.clientId && (
									<FormHelperText>
										{'Please select a client to view policies'}
									</FormHelperText>
								)}
								<FormHelperText error={getError('policyId')}>
									{getErrorMessage('policyId')}
								</FormHelperText>
							</Grid>
							<Grid size={12}>
								<FormLabel required sx={{ display: 'flex' }}>
									{'Policy Transaction'}
								</FormLabel>
								<PolicyTransactionSelect
									policyId={values.policyId}
									policyTransaction={values.transaction}
									setPolicyTrans={(newPolicy) => {
										setValues({
											...values,
											policyTransactionId: newPolicy?.Id,
											transaction: newPolicy,
											insurer: newPolicy?.InsurerName,
											[YEAR_FOLDER]: getYearFolderFormat(
												newPolicy?.FromDate ?? newPolicy?.PolicyFromDate,
											),
										});
									}}
									disabled={Object.keys(existingTransaction ?? {}) > 0 || loading}
								/>
								{!values.policyId && (
									<FormHelperText>
										{'Please select a policy to view policy transactions'}
									</FormHelperText>
								)}
								<FormHelperText error={getError('policyTransactionId')}>
									{getErrorMessage('policyTransactionId')}
								</FormHelperText>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<ClaimTypeSelect
									disabled={isSubmitting}
									claimType={values.claimType}
									setClaimType={(value) => setFieldValue('claimType', value)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormLabel htmlFor='subjectOfLossDetails'>
									{'Subject of Loss details'}
								</FormLabel>
								<TextField
									fullWidth
									id='subjectOfLossDetails'
									{...getFieldProps('subjectOfLossDetails')}
									disabled={loading}
									error={getError('subjectOfLossDetails')}
									multiline
									inputProps={{ maxLength: 100 }}
								/>
								<Grid container>
									<Grid size={10}>
										<FormHelperText error={getError('subjectOfLossDetails')}>
											{getErrorMessage('subjectOfLossDetails') ||
												'Please provide specific details about what was lost or damaged'}
										</FormHelperText>
									</Grid>
									<Grid size={2}>
										<FormHelperText
											sx={{
												textAlign: 'right',
												mr: '14px',
												...(values.subjectOfLossDetails?.length == 100 && {
													fontWeight: 'bold',
												}),
											}}
										>
											{`${values.subjectOfLossDetails?.length || 0}/100`}
										</FormHelperText>
									</Grid>
								</Grid>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormLabel htmlFor='repairer'>{'Repairer'}</FormLabel>
								<TextField
									fullWidth
									id='repairer'
									{...getFieldProps('repairer')}
									disabled={loading}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormLabel htmlFor='assessor'>{'Assessor'}</FormLabel>
								<TextField
									fullWidth
									id='assessor'
									{...getFieldProps('assessor')}
									disabled={loading}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormLabel>{'Insurer'}</FormLabel>
								<TextField
									fullWidth
									{...getFieldProps('insurer')}
									disabled={true}
									value={values.insurer}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormLabel htmlFor='dateOfLoss' required>
									{'Date of loss'}
								</FormLabel>
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DatePicker
										name='dateOfLoss'
										sx={{ width: '100%' }}
										disabled={
											!values.policyId ||
											!values.policyTransactionId ||
											isSubmitting
										}
										format='dd/MM/yyyy'
										disableFuture={true}
										value={values.dateOfLoss}
										slotProps={{
											textField: { id: 'dateOfLoss' },
										}}
										{...getFieldProps('dateOfLoss')}
										onChange={(value) => {
											setFieldValue('dateOfLoss', value);
										}}
									/>
								</LocalizationProvider>
								<FormHelperText error={getError('dateOfLoss')}>
									{getErrorMessage('dateOfLoss')}
								</FormHelperText>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormLabel htmlFor='dateReported' required>
									{'Date Reported'}
								</FormLabel>
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DatePicker
										name='dateReported'
										sx={{ width: '100%' }}
										disabled={isSubmitting}
										format='dd/MM/yyyy'
										disableFuture={true}
										value={values.dateReported}
										slotProps={{
											textField: { id: 'dateReported' },
										}}
										{...getFieldProps('dateReported')}
										onChange={(value) => {
											setFieldValue('dateReported', value);
										}}
									/>
								</LocalizationProvider>
								<FormHelperText error={getError('dateReported')}>
									{getErrorMessage('dateReported')}
								</FormHelperText>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormLabel htmlFor='describeClaim' required>
									{'Brief description of the incident'}
								</FormLabel>
								<TextField
									fullWidth
									id='describeClaim'
									{...getFieldProps('describeClaim')}
									disabled={loading}
									error={getError('describeClaim')}
									// helperText={getErrorMessage('describeClaim')}
									inputProps={{ maxLength: 100 }}
								/>
								<Grid container>
									<Grid size={10}>
										<FormHelperText error={getError('describeClaim')}>
											{getErrorMessage('describeClaim')}
										</FormHelperText>
									</Grid>
									<Grid size={2}>
										<FormHelperText
											sx={{
												textAlign: 'right',
												mr: '14px',
												...(values.describeClaim.length == 100 && {
													fontWeight: 'bold',
												}),
											}}
										>
											{`${values.describeClaim.length}/100`}
										</FormHelperText>
									</Grid>
								</Grid>
							</Grid>
							<Grid size={12}>
								<FormLabel>{'Additional Notes'}</FormLabel>
								<TextField
									fullWidth
									id='notes'
									multiline
									rows={4}
									{...getFieldProps('notes')}
									disabled={loading}
								/>
							</Grid>
							<Grid size={12}>
								<FileUpload
									files={values.files}
									setFiles={(files) =>
										handleChange({
											target: {
												name: 'files',
												value: files,
											},
										})
									}
									loading={loading}
								/>
							</Grid>
							<Grid size={12}>
								<DealFormMessages />
							</Grid>
						</Grid>
					)}
				</CardContent>
				<Divider />
				<DealFormActions disabled={isUserLoading || loading} />
			</Form>
		</Container>
	);
};
